import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { AccountType, InvestmentPackageSettings } from 'api-client';
import lightningIcon from 'assets/images/svg/lightning.svg';
import { Checkbox } from 'components/common/form';
import { CheckIcon, ChevronIcon, CrossIcon } from 'components/common/icons';
import { Loader } from 'components/common/utils';
import { createAccountManager, createInvestmentPackageManager } from 'di';
import { useErrorCallback } from 'hooks';
import { cn } from 'utils';

import { PrimaryButton } from '../../buttons';
import { SecondaryButton } from '../../buttons/decorators';
import { PrimaryButtonColor } from '../../buttons/decorators/PrimaryButton/PrimaryButton';
import { SecondaryButtonColor } from '../../buttons/decorators/SecondaryButton/SecondaryButton';
import { buildSettingsBullets } from '../comfortAccountSettings';
import Modal from '../Modal';
import { ModalProps } from '../Modal/Modal';
import styles from './ComfortAccountWizardModal.module.scss';

const COMFORT_ACCOUNT_RULES_URL =
  'https://cdn.conomica.ru/file/3176f/c27/159d-3b4c7/pravila-scet-komfort-2.pdf';

type WizardStep = 'info' | 'loading' | 'result';

type ResultState =
  | { kind: 'success' }
  | {
      kind: 'error';
      message: string;
    };

type Props = ModalProps & {
  accountOpened?: boolean;
  onAccountOpened?: () => void;
  onTopup?: () => void;
};

const ComfortAccountWizardModal: FC<Props> = ({
  active,
  onClose,
  accountOpened,
  onAccountOpened,
  onTopup,
}) => {
  const handleError = useErrorCallback();
  const [step, setStep] = useState<WizardStep>('info');
  const [settings, setSettings] = useState<InvestmentPackageSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);
  const [agree, setAgree] = useState(false);
  const handleErrorRef = useRef(handleError);
  const settingsLoadedRef = useRef(false);
  const isOpeningRef = useRef(false);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (!active) {
      settingsLoadedRef.current = false;
      isOpeningRef.current = false;
      return;
    }

    if (settingsLoadedRef.current) {
      return;
    }

    settingsLoadedRef.current = true;
    setStep('info');
    setResult(null);
    setAgree(false);
    setSettings(null);
    setSettingsLoading(true);

    const loadSettings = async () => {
      try {
        const manager = await createInvestmentPackageManager();
        setSettings(await manager.getSettings());
      } catch (error) {
        if (error instanceof Error) {
          await handleErrorRef.current(error);
        }
      } finally {
        setSettingsLoading(false);
      }
    };

    void loadSettings();
  }, [active]);

  const bullets = useMemo(() => (settings ? buildSettingsBullets(settings) : []), [settings]);

  const close = () => onClose();

  const openComfortAccount = async () => {
    if (isOpeningRef.current) {
      return;
    }

    isOpeningRef.current = true;
    setStep('loading');

    try {
      const accountManager = await createAccountManager();
      await accountManager.create(AccountType.COMFORT);
      setResult({ kind: 'success' });
      onAccountOpened?.();
    } catch (error) {
      if (error instanceof Error) {
        await handleErrorRef.current(error);
      }
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'К сожалению, открыть счет не получилось',
      });
    } finally {
      isOpeningRef.current = false;
      setStep('result');
    }
  };

  const handleReturnHome = () => {
    close();
  };

  const handleTopup = () => {
    close();
    onTopup?.();
  };

  const handleRetry = () => {
    setResult(null);
    setStep('info');
  };

  const showClose = step !== 'loading';
  const showBack = step !== 'loading';

  const goBack = () => {
    if (step === 'result' && result?.kind === 'error') {
      handleRetry();
      return;
    }
    close();
  };

  return (
    <Modal
      active={active}
      onClose={onClose}
      className={cn(
        styles.modal,
        step === 'result' ? styles.modalComfortWizardResult : styles.modalComfortWizard
      )}
    >
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {showBack ? (
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="Назад">
              <ChevronIcon direction="left" color="var(--white-color)" />
            </button>
          ) : (
            <div className={styles.headerLeftSpacer} />
          )}
        </div>
        <div className={styles.headerTitle}>Условия использования счета Комфорт</div>
        <div className={styles.headerRight}>
          {showClose ? (
            <button
              type="button"
              className={styles.closeButton}
              onClick={close}
              aria-label="Закрыть"
            >
              <CrossIcon preset="default" />
            </button>
          ) : (
            <div className={styles.headerRightSpacer} />
          )}
        </div>
      </div>

      <div className={styles.body}>
        {step === 'info' ? (
          <div className={styles.infoStep}>
            {settingsLoading ? (
              <div className={styles.centerState}>
                <Loader size="large" overlay={false} />
              </div>
            ) : !settings ? (
              <div className={styles.centerState}>
                <div className={styles.resultText}>Не удалось загрузить настройки счета</div>
              </div>
            ) : (
              <>
                <div className={styles.bullets}>
                  {bullets.map((bullet) => (
                    <div
                      key={bullet.plain ?? bullet.chip ?? bullet.beforeChip}
                      className={styles.bulletRow}
                    >
                      <img
                        className={styles.bulletIcon}
                        src={lightningIcon}
                        alt=""
                        aria-hidden="true"
                      />
                      <div className={styles.bulletText}>
                        {bullet.plain ? (
                          bullet.plain
                        ) : (
                          <>
                            {bullet.beforeChip}
                            {bullet.chip ? (
                              <span className={styles.chip}>{bullet.chip}</span>
                            ) : null}
                            {bullet.afterChip}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        {step === 'loading' ? (
          <div className={styles.centerState}>
            <Loader size="large" overlay={false} />
          </div>
        ) : null}

        {step === 'result' ? (
          <div className={styles.resultState}>
            {result?.kind === 'success' ? (
              <div className={styles.resultRow}>
                <CheckIcon preset="circle-success" size={24} />
                <div className={styles.resultText}>Счет успешно открыт</div>
              </div>
            ) : (
              <div className={styles.resultRow}>
                <CrossIcon preset="circle-danger" size={24} />
                <div className={styles.resultText}>
                  {result?.message ?? 'К сожалению, открыть счет не получилось'}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className={cn(styles.footer, step === 'loading' ? styles.footerHidden : '')}>
        {step === 'info' && !settingsLoading ? (
          <>
            {!accountOpened ? (
              <>
                <PrimaryButton
                  color={
                    agree && !settingsLoading ? PrimaryButtonColor.GREEN : PrimaryButtonColor.GRAY
                  }
                  expanded
                  large
                >
                  <button
                    type="button"
                    onClick={() => void openComfortAccount()}
                    disabled={!agree || settingsLoading}
                  >
                    Активировать счет Комфорт
                  </button>
                </PrimaryButton>
                <div className={styles.agreeRow}>
                  <Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)}>
                    <span className={styles.agreeText}>
                      Согласен с{' '}
                      <a
                        href={COMFORT_ACCOUNT_RULES_URL}
                        className={styles.agreeLinkGreen}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Правилами пользования счета Комфорт
                      </a>
                    </span>
                  </Checkbox>
                </div>
              </>
            ) : (
              <>
                <div className={styles.buttonRow}>
                  <PrimaryButton color={PrimaryButtonColor.GRAY} expanded large>
                    <button type="button" disabled>
                      Счет комфорт подключен
                    </button>
                  </PrimaryButton>
                  <PrimaryButton color={PrimaryButtonColor.GREEN} expanded large>
                    <button type="button" onClick={handleTopup}>
                      Создать пакет
                    </button>
                  </PrimaryButton>
                </div>
                <div className={styles.agreeRow}>
                  <span className={styles.agreeText}>
                    <a
                      href={COMFORT_ACCOUNT_RULES_URL}
                      className={styles.agreeLinkGreen}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Правила пользования счета Комфорт
                    </a>
                  </span>
                </div>
              </>
            )}
          </>
        ) : null}

        {step === 'result' && result?.kind === 'success' ? (
          <div className={styles.footerRow}>
            <SecondaryButton color={SecondaryButtonColor.GREEN} className={styles.footerSecondary}>
              <button type="button" onClick={handleReturnHome}>
                Вернуться на главную
              </button>
            </SecondaryButton>
            <PrimaryButton color={PrimaryButtonColor.GREEN} expanded large>
              <button type="button" onClick={handleTopup}>
                Создать пакет сделок
              </button>
            </PrimaryButton>
          </div>
        ) : null}

        {step === 'result' && result?.kind === 'error' ? (
          <div className={styles.footerRow}>
            <SecondaryButton color={SecondaryButtonColor.GREEN} className={styles.footerSecondary}>
              <button type="button" onClick={handleReturnHome}>
                Вернуться на главную
              </button>
            </SecondaryButton>
            <PrimaryButton color={PrimaryButtonColor.GREEN} expanded large>
              <button type="button" onClick={handleRetry}>
                Попробовать снова
              </button>
            </PrimaryButton>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default ComfortAccountWizardModal;
